import { ReactNode, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { selectOtherProjects, UserContextRootState } from 'core/userContext';

import { BUTTON_SIZES, Button } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { WarningBanner } from 'ui/banner';
import { MODAL_SIZES, MODAL_VARIANTS, ModalFooter, ModalHeader } from 'ui/modal';
import { BaseOption } from 'ui/selects';

import noOp from 'utils/noop';

import { addPositiveToast } from 'modules/toastContainer';
import { InputHint } from '../Styled';
import { useCreateSnippet } from '../../hooks/useCreateSnippet';
import { useCloneSnippet } from '../../hooks/useCloneSnippet';
import { useSnippetQueryContext } from '../../context/SnippetQueryContext';
import { GraphQLSnippet, SnippetFieldsInputIds, ValidationData } from '../../types';
import { ModalIntent } from '../../constants';
import { checkValidSnippetName, getRemainingCharactersText } from '../../utils';
import { objFilterOut } from '@iterable/utils';

import {
  Body2Label,
  RadioContainer,
  RadioOption,
  StyledRadio,
  Body2Span,
  Spacer,
  OverflowModal,
  OverflowBody,
} from './CopySnippetModal.styles';
// style imported from template modal to reduce redundency
import { LabelledSelect } from '../../../templateIndex/templateModals/components/LabelledSelect';

type ModalContent = {
  copy: string;
  header: string;
  helpTextPrimary: string;
  helpTextSecondary: string;
  label: string;
  radio: {
    header: string;
    curProjText: string;
    otherProjText: string;
  };
  projSelect: {
    title: string;
    placeholder: string;
    empty: string;
  };
  nestedSnippetWarning: {
    message: string;
  };
};

export const modalContent: ModalContent = {
  copy: 'Copy snippet',
  header: 'Copy snippet?',
  helpTextPrimary: "Must be unique and can't be changed later.",
  helpTextSecondary: 'You can use letters, spaces, dashes, and underscores.',
  label: 'New snippet name',
  radio: {
    header: 'Copy to',
    curProjText: 'Current project',
    otherProjText: 'Another project',
  },
  projSelect: {
    title: 'Select project',
    placeholder: 'Project name',
    empty: 'No other projects',
  },
  nestedSnippetWarning: {
    message:
      'This snippet contains nested snippets. You will need to copy these snippets separately to work within the new project.',
  },
};

const RADIO_OPTIONS = {
  UNSELECTED: 'unselected',
  CURRENT_PROJECT: 'current project',
  ANOTHER_PROJECT: 'another project',
};

type Props = {
  closeModal: () => void;
  intent?: ModalIntent;
  onCopySuccess: (updatedSnippet: GraphQLSnippet) => void;
  snippet: GraphQLSnippet;
};

export const CopySnippetModal = ({ closeModal, intent, onCopySuccess, snippet }: Props) => {
  const { refetch } = useSnippetQueryContext();
  const dispatch = useDispatch();
  /** Tracks errors in snippet fields upon validation */
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string | ReactNode }>({});
  const [newSnippetName, setNewSnippetName] = useState(`${snippet.name}_copy`);
  const [destProjectId, setDestProjectId] = useState<number>();
  const [checkedRadio, setCheckedRadio] = useState(RADIO_OPTIONS.CURRENT_PROJECT);
  const isAnotherProjectSelected = checkedRadio === RADIO_OPTIONS.ANOTHER_PROJECT;
  // checks if snippet has a nested snippet
  const isSnippetNested: boolean = snippet.content.match(/snippet/g) !== null;
  // mark whether the user has been warned or not about the nested snippet
  const [isUserWarned, setIsUserWarned] = useState<boolean>(false);

  // selectors used to fetch data for copy snippet to another project and then maps to select options
  const projects = useSelector<UserContextRootState, BaseOption<number>[]>((state) =>
    selectOtherProjects(state).map(({ id, name }) => ({ label: name, id: `${id}`, value: id })),
  );

  const destProjectName: string | undefined = useMemo(() => {
    const destProject = projects.find((project) => project.value === destProjectId);
    return destProject?.label as string;
  }, [destProjectId, projects]);

  const resetToDefaultState = () => {
    setFieldErrors((fldErrs) => objFilterOut(fldErrs, [SnippetFieldsInputIds.name]));
    setDestProjectId(undefined);
    setIsUserWarned(false);
  };

  const handleFieldChange = (fieldId: string, validationError = '') => {
    // Remove field from errors upon change
    if (validationError) {
      setFieldErrors((fldErrs) => ({ ...fldErrs, [fieldId]: validationError }));
    } else {
      setFieldErrors((fldErrs) => objFilterOut(fldErrs, [fieldId]));
    }
  };

  const handleSnippetName = (value: string) => {
    setNewSnippetName(value);
    handleFieldChange(
      SnippetFieldsInputIds.name,
      !checkValidSnippetName(value) ? 'Invalid snippet name.' : '',
    );
  };

  const onErrorHandler = (errors: ValidationData[]) => {
    // completely reset the errors based on the error return
    setFieldErrors(
      errors.reduce(
        (errorMapping, error) => ({ ...errorMapping, [error.fieldId]: error.errorMsg }),
        {},
      ),
    );
  };

  const onSuccessHandler = (updatedSnippet: GraphQLSnippet) => {
    // on clone to another project success
    if (isAnotherProjectSelected) {
      const toastMessage = `Your snippet was successfully copied to ${destProjectName}.`;
      const options = {
        actions: [
          {
            title: 'Click here to view it.',
            href: `/templates/snippets?projectId=${destProjectId}`,
          },
        ],
      };
      dispatch(addPositiveToast(toastMessage, options));
    }
    // on copy within project success
    else if (intent === ModalIntent.edit) {
      onCopySuccess(updatedSnippet);
    } else {
      refetch();
    }
    closeModal();
  };

  const [createSnippet] = useCreateSnippet({
    onComplete: noOp,
    onError: (errors) => {
      onErrorHandler(errors);
    },
    onSuccess: (updatedSnippet) => {
      onSuccessHandler(updatedSnippet);
    },
  });

  const [cloneSnippet] = useCloneSnippet({
    onComplete: noOp,
    onError: (errors) => {
      onErrorHandler(errors);
    },
    onSuccess: (updatedSnippet) => {
      onSuccessHandler(updatedSnippet);
    },
  });

  const handleSaveSnippet = () => {
    if (isSnippetNested && !isUserWarned && isAnotherProjectSelected) {
      setIsUserWarned(true);
      return;
    }

    if (!isAnotherProjectSelected) {
      createSnippet({
        variables: {
          name: newSnippetName,
          fields: {
            content: snippet.content,
            description: snippet.description,
            positionalParameters: snippet.positionalParameters,
          },
        },
      });
    } else {
      cloneSnippet({
        variables: {
          name: newSnippetName,
          destinationProjectId: destProjectId,
          fields: {
            content: snippet.content,
            description: snippet.description,
            name: newSnippetName,
            positionalParameters: snippet.positionalParameters,
          },
        },
      });
    }
  };

  const isSaveSnippetButtonDisabled =
    Object.keys(fieldErrors).length > 0 || (isAnotherProjectSelected && !destProjectId);

  return (
    <OverflowModal
      id="copySnippetModal"
      isModalOpen={true}
      onClose={closeModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader autoFocus={false}>{modalContent.header}</ModalHeader>
      <OverflowBody>
        {/* Select a radio button to copy to this project or another project */}
        <Body2Label>{modalContent.radio.header}</Body2Label>
        <RadioContainer>
          <RadioOption>
            <StyledRadio
              data-test="current-project-radio"
              id="current-project-radio"
              isChecked={checkedRadio === RADIO_OPTIONS.CURRENT_PROJECT}
              name="current-project-radio"
              value={RADIO_OPTIONS.CURRENT_PROJECT}
              onClick={() => {
                setCheckedRadio(RADIO_OPTIONS.CURRENT_PROJECT);
                resetToDefaultState();
              }}
            />
            <Body2Span
              as="label"
              htmlFor="current-project-radio"
              onClick={() => {
                setCheckedRadio(RADIO_OPTIONS.CURRENT_PROJECT);
                resetToDefaultState();
              }}
            >
              {modalContent.radio.curProjText}
            </Body2Span>
          </RadioOption>
          <RadioOption>
            <StyledRadio
              data-test="another-project-radio"
              id="another-project-radio"
              isChecked={isAnotherProjectSelected}
              name="another-project-radio"
              value={RADIO_OPTIONS.ANOTHER_PROJECT}
              onClick={() => setCheckedRadio(RADIO_OPTIONS.ANOTHER_PROJECT)}
            />
            <Body2Span
              as="label"
              htmlFor="another-project-radio"
              onClick={() => setCheckedRadio(RADIO_OPTIONS.ANOTHER_PROJECT)}
            >
              {modalContent.radio.otherProjText}
            </Body2Span>
          </RadioOption>
        </RadioContainer>
        <Spacer />

        {/* Conditionally render project select if copying to another project */}
        {/* Project to copy to select */}
        {isAnotherProjectSelected && (
          <>
            <LabelledSelect
              title={modalContent.projSelect.title}
              id="snippet-project-select"
              data-test="snippet-project-select"
              placeholder={modalContent.projSelect.placeholder}
              empty={modalContent.projSelect.empty}
              options={projects}
              onSelect={setDestProjectId}
            />
            <Spacer />
          </>
        )}

        <FormField
          errorText={fieldErrors[SnippetFieldsInputIds.name]}
          hasError={SnippetFieldsInputIds.name in fieldErrors}
          helpText={
            <InputHint>
              <span>{modalContent.helpTextPrimary}</span> {modalContent.helpTextSecondary}
            </InputHint>
          }
          labelText={modalContent.label}
          restrictionText={getRemainingCharactersText(100, newSnippetName.length)}
        >
          <InputText
            id="snippet-name-field"
            maxLength={100}
            onChange={handleSnippetName}
            type="text"
            value={newSnippetName}
          />
        </FormField>

        {/* Conditionally render nested snippet warning if the snippet is nested, the user has 
        been warned, and the user is attempting to copy the snippet into another project */}
        {isSnippetNested && isUserWarned && isAnotherProjectSelected && (
          <>
            <Spacer />
            <WarningBanner>
              <div>{modalContent.nestedSnippetWarning.message}</div>
            </WarningBanner>
          </>
        )}
      </OverflowBody>

      <ModalFooter>
        <Button
          isDisabled={isSaveSnippetButtonDisabled}
          onClick={handleSaveSnippet}
          size={BUTTON_SIZES.LARGE}
        >
          {modalContent.copy}
        </Button>
      </ModalFooter>
    </OverflowModal>
  );
};
